import {BaseService, DataBaseServiceResponse, ServiceLoopbackFilters} from '../../services/base-service/base.service';
import {IPhysicalExaminationSignCategory} from './physical-examination-sign-category.interface';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {firstValueFrom} from 'rxjs';
import {IPhysicalExaminationSign} from './physical-examination-sign.interface';
import {IRecordNutritionalPhysicalExamination} from './record-nutritional-physical-examination.interface';

export interface IRNutritionalPhysicalExamination {
  nutritionalPhysicalExamination: IRecordNutritionalPhysicalExamination;
  physicalExaminationSignCategories: IPhysicalExaminationSignCategory[];
  physicalExaminationSigns: IPhysicalExaminationSign[];
  wasDeployed?: boolean;
}

export namespace RecordNutritionalPhysicalExaminationAction {
  export class GetActive {
    static readonly type = '[Record] Get Nutritional Physical Examination Action';
    constructor(public serviceId: string, public patientId: string) { }
  }

  export class GetCategories {
    static readonly type = '[Record] Get Physical Examination Sign Categories Action';
    constructor(public filter: ServiceLoopbackFilters) { }
  }

  export class PatchActive {
    static readonly type = '[Record] Patch Nutritional Physical Examination Action';
    constructor(public serviceId: string, public patientId: string, public data: { signIds?: string[], information?: string }) { }
  }

  export class ResetState {
    static readonly type = '[Record] Reset Nutritional Physical Examination Action';
    constructor() { }
  }

  export class Deploy {
    static readonly type = '[Record] Was Deployed Record Nutritional Physical Examination Action';
    constructor(public value: boolean) { }
  }
}



@State<IRNutritionalPhysicalExamination>({
  name: 'NutritionalPhysicalExaminationState',
  defaults: {
    nutritionalPhysicalExamination: null,
    physicalExaminationSignCategories: [],
    physicalExaminationSigns: []
  },
})

@Injectable()
export class NutritionalPhysicalExaminationState {
  constructor(
    private baseService: BaseService<any>,
  ) {
  }

  url = environment.SERVER_URL;

  @Selector()
  public static getNutritionalPhysicalExamination(state: IRNutritionalPhysicalExamination): IRecordNutritionalPhysicalExamination {
    return state.nutritionalPhysicalExamination;
  }

  @Selector()
  public static getPhysicalExaminationSignCategories(state: IRNutritionalPhysicalExamination): IPhysicalExaminationSignCategory[] {
    return state.physicalExaminationSignCategories;
  }

  @Selector()
  public static getWasDeployed(state: IRNutritionalPhysicalExamination): boolean {
    return state.wasDeployed;
  }

  @Action(RecordNutritionalPhysicalExaminationAction.GetActive)
  async getRecordNutritionalPhysicalExaminationAction(
    ctx: StateContext<IRNutritionalPhysicalExamination>,
    action: RecordNutritionalPhysicalExaminationAction.GetActive
  ): Promise<void> {
    const response: DataBaseServiceResponse<IRecordNutritionalPhysicalExamination> = await firstValueFrom(
      this.baseService.get(`${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/nutritional-physical-examination/active`)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      nutritionalPhysicalExamination: response.entity
    });
  }

  @Action(RecordNutritionalPhysicalExaminationAction.GetCategories)
  async getPhysicalExaminationSignCategoriesAction(
    ctx: StateContext<IRNutritionalPhysicalExamination>,
    action: RecordNutritionalPhysicalExaminationAction.GetCategories
  ): Promise<void> {
    if (ctx.getState()?.physicalExaminationSignCategories?.length > 0) return;
    const response: DataBaseServiceResponse<IPhysicalExaminationSignCategory[]> = await firstValueFrom(
      this.baseService.get(`${this.url}/physical-examination-signs/categories`, action.filter)
    );

    if (response.error) throw new Error(response.message);

    ctx.patchState({
      physicalExaminationSignCategories: response.entity
    });
  }

  @Action(RecordNutritionalPhysicalExaminationAction.PatchActive)
  async patchRecordNutritionalPhysicalExaminationAction(
    ctx: StateContext<IRNutritionalPhysicalExamination>,
    action: RecordNutritionalPhysicalExaminationAction.PatchActive
  ): Promise<void> {
    const response: DataBaseServiceResponse<void> = await firstValueFrom(
      this.baseService.patch(
        `${this.url}/patients/${action.patientId}/service/${action.serviceId}/records/nutritional-physical-examination`,
        action.data
      )
    );

    if (response.error) throw new Error(response.message);
  }

  @Action(RecordNutritionalPhysicalExaminationAction.Deploy)
  async wasDeployedRecordNutritionalPhysicalExaminationAction(
    ctx: StateContext<IRNutritionalPhysicalExamination>,
    action: RecordNutritionalPhysicalExaminationAction.Deploy
  ): Promise<void> {
    ctx.patchState({
      wasDeployed: action.value
    });
  }

  @Action(RecordNutritionalPhysicalExaminationAction.ResetState)
  async resetRecordNutritionalPhysicalExaminationAction(
    ctx: StateContext<IRNutritionalPhysicalExamination>,
    _: RecordNutritionalPhysicalExaminationAction.ResetState
  ): Promise<void> {
    ctx.patchState({
      nutritionalPhysicalExamination: null
    });
  }
}
